/**
 * Uma fila com régua: a página de trânsitos e a contagem de cada cartão, do mesmo universo.
 *
 * A Operação e a SENATRAN são a mesma forma com recortes diferentes — por isso um gancho
 * só. O que ele garante é o `AC04` do lado do cliente: a lista pede
 * `?recorte=<universo>&recorte=<cartão aceso>` e a régua pede `?recorte=<universo>&cartao=…`,
 * com **os mesmos filtros** nas duas. Filtro que entrasse numa chamada e não na outra seria
 * o cartão contando uma fila e o clique abrindo outra.
 *
 * **Paginação no servidor** (`O-08`, parcial): `limite` e `deslocamento`. O total não vem
 * da lista — vem da régua, que já contou o cartão aceso. Uma segunda contagem para o rodapé
 * seria uma terceira definição do mesmo número.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type { ItemDaFila, Regua } from "../api/tipos";
import { EVENTO_DO_RELOGIO } from "../relogio";

export interface PedidoDaFila {
  /** O recorte que define a tela: `na_operacao` ou `senatran`. */
  universo: string;
  /** Os cartões da régua, na ordem da tela. O primeiro costuma ser o próprio universo. */
  cartoes: readonly string[];
  /** O cartão aceso. Igual ao universo quando nenhum está. */
  cartao: string;
  /** Filtros de período e lugar, e o `recorte` que veio de Indicadores, já em query string. */
  filtros: string;
  pagina: number;
  limite: number;
  /**
   * De quantos em quantos milissegundos perguntar se a fila mudou (`C-04`, CA-08). Sem
   * isto, a fila só muda quando alguém clica. A pergunta é barata — a impressão da fila,
   * não a fila —, e a lista e a régua só são pedidas de novo quando a resposta muda.
   */
  vigiarACada?: number;
}

export interface EstadoDaFila {
  itens: ItemDaFila[] | null;
  regua: Regua | null;
  /** Quantos há no cartão aceso — o tamanho da fila inteira, não da página. */
  total: number | null;
  erro: ErroDaApi | null;
  atualizadoEm: string | null;
  recarregar: () => Promise<void>;
}

export function consultaDaLista(pedido: PedidoDaFila): string {
  const p = new URLSearchParams(pedido.filtros);
  p.append("recorte", pedido.universo);
  if (pedido.cartao !== pedido.universo) p.append("recorte", pedido.cartao);
  p.set("limite", String(pedido.limite));
  if (pedido.pagina > 0)
    p.set("deslocamento", String(pedido.pagina * pedido.limite));
  return p.toString();
}

/**
 * A exportação do filtro inteiro (`O-09`): exatamente os parâmetros da lista — filtros,
 * universo, cartão aceso, busca e ordem —, sem página nem limite. É a mesma função que monta
 * a lista, menos a paginação; um conjunto escrito à parte divergiria da tela em silêncio.
 */
export function consultaDaExportacao(
  pedido: Pick<PedidoDaFila, "filtros" | "universo" | "cartao">,
  formato: "csv" | "json",
): string {
  const p = new URLSearchParams(pedido.filtros);
  p.append("recorte", pedido.universo);
  if (pedido.cartao !== pedido.universo) p.append("recorte", pedido.cartao);
  p.set("formato", formato);
  return p.toString();
}

/** A impressão da fila: o universo e os filtros, sem cartão nem página — é a fila inteira. */
export function consultaDaMarca(
  pedido: Pick<PedidoDaFila, "filtros" | "universo">,
): string {
  const p = new URLSearchParams(pedido.filtros);
  p.append("recorte", pedido.universo);
  return p.toString();
}

export function consultaDaRegua(pedido: PedidoDaFila): string {
  const p = new URLSearchParams(pedido.filtros);
  p.append("recorte", pedido.universo);
  const cartoes = new Set([...pedido.cartoes, pedido.cartao]);
  for (const c of cartoes) p.append("cartao", c);
  return p.toString();
}

export function useFila(pedido: PedidoDaFila): EstadoDaFila {
  const [itens, definirItens] = useState<ItemDaFila[] | null>(null);
  const [regua, definirRegua] = useState<Regua | null>(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  const [atualizadoEm, definirAtualizadoEm] = useState<string | null>(null);

  const lista = consultaDaLista(pedido);
  const contas = consultaDaRegua(pedido);

  // Só a resposta do **último** pedido vale (Gisele, 29/09). Decidir recarrega a fila do
  // cartão aceso; clicar em outro cartão logo depois pede a nova — e, se a primeira
  // resposta chegasse por último, a tela mostrava "Em revisão" aceso com a lista de
  // "Pendentes" embaixo, e o que acabou de ir para revisão parecia não estar lá.
  const ultimo = useRef(0);
  const consultaAtual = useRef(lista);
  useEffect(() => {
    // Mudou o cartão ou o filtro: a lista de antes não é a desta consulta. Some, e a
    // tela diz "carregando" em vez de mostrar os itens do cartão anterior.
    if (consultaAtual.current !== lista) {
      consultaAtual.current = lista;
      definirItens(null);
    }
  }, [lista]);

  const recarregar = useCallback(async () => {
    const pedido = ++ultimo.current;
    try {
      const [novos, contagem] = await Promise.all([
        api<ItemDaFila[]>(`/tollvision/transitos?${lista}`),
        api<Regua>(`/tollvision/regua?${contas}`),
      ]);
      if (pedido !== ultimo.current) return;
      definirItens(novos);
      definirRegua(contagem);
      definirErro(null);
      definirAtualizadoEm(new Date().toLocaleTimeString("pt-BR"));
    } catch (falha) {
      if (pedido !== ultimo.current) return;
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
      definirItens(null);
      definirRegua(null);
    }
  }, [lista, contas]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // O relógio da demonstração andou (ADR-059): os prazos e as janelas mudaram de lugar.
  useEffect(() => {
    const andar = () => void recarregar();
    window.addEventListener(EVENTO_DO_RELOGIO, andar);
    return () => window.removeEventListener(EVENTO_DO_RELOGIO, andar);
  }, [recarregar]);

  // A fila que se atualiza sozinha (C-04). Pergunta a impressão; se mudou desde a última
  // resposta, recarrega. Aba escondida não pergunta — ninguém está olhando, e ao voltar a
  // primeira pergunta já traz a diferença.
  const marca = consultaDaMarca(pedido);
  const intervalo = pedido.vigiarACada ?? 0;
  useEffect(() => {
    if (intervalo <= 0) return;
    let anterior: string | null = null;
    let vivo = true;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void api<{ marca: string }>(`/tollvision/marca?${marca}`)
        .then(async ({ marca: atual }) => {
          if (!vivo) return;
          const mudou = anterior !== null && atual !== anterior;
          anterior = atual;
          if (mudou) await recarregar();
        })
        .catch(() => {
          // Uma pergunta que falhou não é motivo para mexer na tela: a próxima tenta.
        });
    }, intervalo);
    return () => {
      vivo = false;
      window.clearInterval(id);
    };
  }, [marca, intervalo, recarregar]);

  return {
    itens,
    regua,
    total: regua ? (regua[pedido.cartao] ?? null) : null,
    erro,
    atualizadoEm,
    recarregar,
  };
}
